import { Component, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { Subject, Subscription } from 'rxjs';

// Models
import { IEvent } from 'src/app/models/event.model';
import { ITicketType } from 'src/app/models/ticket.model';

// Services
import { AuthService, EventService, OrganisationService, PassCultureService } from 'src/app/providers';
import { TicketTypeService } from 'src/app/providers';
import { NotificationService } from 'src/app/services/notification.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { GetEvent } from '../../getEvent';
import { GetParams } from 'src/app/models/type.definition';
import { EventCategory, EventCategoryEnum, IEventOffer, MusicTypeEnum, ShowTypeEnum } from 'src/app/models/passculture.model';
import { IOrganisation } from 'src/app/models/organisation.model';
import { environment } from 'src/environments/environment';

// Interfaces for categories
interface Category {
  id: string;
  name: string;
}

interface Subcategory extends Category {}

@Component({
  selector: 'app-event-ticketing-pass-culture',
  templateUrl: './event-ticketing-pass-culture.component.html',
  styleUrls: ['./event-ticketing-pass-culture.component.scss'],
  providers: [GetEvent]
})
export class EventTicketingPassCultureComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild(ImageCropperComponent) imageCropper: ImageCropperComponent;

    public loadingState$: Subject<void> = new Subject<void>();

  // Form
  passCultureForm: FormGroup;
  isLoading = false;
  dropDown: boolean = false;
  // Data
  event: IEvent;
  eventOffer: IEventOffer | null = null;
  ticketTypes: ITicketType[] = [];
  selectedTicketTypes: string[] = [];
  public currentUrl: string;
  public isAdmin: boolean;
  public checkedTicketTypes: string[] = [];
  public loaded: boolean = false;
  public availableCategories: string[] = Object.values(EventCategoryEnum);
  public availableShowTypes: string[] = Object.values(ShowTypeEnum);
  public availableMusicTypes: string[] = Object.values(MusicTypeEnum);
  public currentImage: Blob;
  public newImage: Blob;
  public loadingImage: boolean = false;
  public organisation: IOrganisation
  public venueEnabled: boolean | null = null;
  public passCultureUrl = environment.passCultureUrl;
  public siret: string;
  // Image handling
  public imageChangedEvent: any = '';
  public croppedImage: any = '';
  public imagePreview: string | ArrayBuffer | null = null;
  public showCropper = false;
  public maxFileSize = 5 * 1024 * 1024; // 5MB

  private _subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private eventService: EventService,
    private ticketTypeService: TicketTypeService,
    private notificationService: NotificationService,
    private errorHandler: ErrorHandlerService,
    private getEvent: GetEvent,
    private authService: AuthService,
    private passCultureService: PassCultureService,
    private organisationService: OrganisationService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.currentUrl = this.router.url;
    await this.checkIfVenueExists()
    await this.getCurrentEvent(true);
    this.isAdmin = this.authService.isAdmin;
    await this.loadTicketTypes();
    this.initForm();
    this.loaded = true;
  }

  goBack(): void {
    this.router.navigate([`evenements/${this.event._id}/billetterie`]);
  }


  async checkIfVenueExists() {
    this.organisation = await this.organisationService.getCurrentOrganisation().toPromise()
    if (this.organisation?.billingInfo?.siret) {
      this.siret = this.organisation.billingInfo.siret;
      try {
        const venue = await this.passCultureService.getVenue(this.siret).toPromise()
        this.venueEnabled = true;
        return true;
      } catch (error) {
        this.notificationService.newNotification({
          message: "Impossible de récupérer votre compte Pass Culture. Veuillez vérifier votre espace culturel.",
          state: "error"
        })
      }
    }
    this.venueEnabled = false;
    return false;
  }

  async getCurrentEvent(reset = false): Promise<void> {
      const eventOptions: GetParams<IEvent> = {
        select: ['name', 'ticketing', 'emailAlerts', 'commission', 'isAccountingSent', 'approvalSettings', 'passCultureSettings'],
      };
      this.event = await this.getEvent.get(eventOptions, reset);
      if (this.event?.passCultureSettings?.isEnabled) {
        try {
          this.eventOffer = await this.passCultureService.getEventOffer(this.event._id).toPromise();
          console.log(this.eventOffer);

          if (this.eventOffer.image.url) {
            this.loadingImage = true;
            // Load image from URL and get Blob
            this.passCultureService.getEventOfferImage(this.event._id).subscribe((response) => {
                this.currentImage = response;
                this.loadingImage = false;
              }, (error) => {
                this.loadingImage = false;
                this.notificationService.newNotification({
                    message: "Une erreur est survenue lors de la récupération de l'image de l'offre Pass Culture",
                  state: "error"
                })
              });
          }
        } catch (error) {
          this.notificationService.newNotification({
              message: "Une erreur est survenue lors de la récupération de l'offre Pass Culture",
            state: "error"
          })
        }
      }
}

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private initForm(): void {

    this.passCultureForm = this.fb.group({
      isEnabled: [this.event.passCultureSettings?.isEnabled || false],
      ticketTypeIds: [this.event.passCultureSettings?.ticketTypeIds || []],
      enableDoubleBookings: [this.eventOffer?.enableDoubleBookings || false],
      category: [this.eventOffer?.categoryRelatedFields.category || ''],
      showType: [this.eventOffer?.categoryRelatedFields.showType || undefined],
      musicType: [this.eventOffer?.categoryRelatedFields.musicType || undefined],
      author: [this.eventOffer?.categoryRelatedFields.author || ''],
      performer: [this.eventOffer?.categoryRelatedFields.performer || ''],
      stageDirector: [this.eventOffer?.categoryRelatedFields.stageDirector || ''],
      bookingContact: [this.eventOffer?.bookingContact || '', [Validators.required]],
      speaker: [this.eventOffer?.categoryRelatedFields.speaker || ''],
      visa: [this.eventOffer?.categoryRelatedFields.visa || ''],
      accessibility: this.fb.group({
        audioDisabilityCompliant: [this.eventOffer?.accessibility.audioDisabilityCompliant || false],
        mentalDisabilityCompliant: [this.eventOffer?.accessibility.mentalDisabilityCompliant || false],
        motorDisabilityCompliant: [this.eventOffer?.accessibility.motorDisabilityCompliant || false],
        visualDisabilityCompliant: [this.eventOffer?.accessibility.visualDisabilityCompliant || false],
      })
    });

    // Initialize checkedTicketTypes if there are any saved ticketTypeIds
    if (this.event.passCultureSettings?.ticketTypeIds) {
      this.checkedTicketTypes = [...this.event.passCultureSettings.ticketTypeIds];
    }

    this.passCultureForm.get('category').valueChanges.subscribe(category => {
        if (this.showSubCategory('showType')) {
            this.passCultureForm.get('showType').setValidators([Validators.required]);
        }
        else {
            this.passCultureForm.get('showType').clearValidators();
        }
    })

  }

  @HostListener('document:click') clickDoc(): void {
    this.dropDown = false;
  }

  public getTicketsName(): string {
    const names = this.ticketTypes.filter(_ => this.checkedTicketTypes.includes(_._id));
    if (!names.length) {
      return `Séléctionner des tickets`;
    }
    else {
      const fullString = names.map(_ => _.name).join(', ');
      return fullString.length < 80 ? fullString : `${fullString.substring(0, 77)} ...`
    }

  }

  public ticketChecked(id: string): boolean {
    return this.checkedTicketTypes.includes(id) || null;
  }

  public checkTicket(id: string, value: boolean): void {
    const inArray = this.checkedTicketTypes.includes(id);
    if (value && !inArray) {
      this.checkedTicketTypes.push(id);
    }
    else if (!value && inArray) {
      this.checkedTicketTypes = this.checkedTicketTypes.filter(_ => _ !== id);
    }

    this.passCultureForm.get('ticketTypeIds').setValue(this.checkedTicketTypes);
  }

  private async loadTicketTypes() {
    const result = await this.ticketTypeService.getList({
        filter: { eventId: this.event._id },
        populate: ['ticketTypeCategoryId'],
        limit: 500
      }).toPromise();
    this.ticketTypes = result.data;
  }

  // Ticket type selection
  onTicketTypeSelect(event: any, ticketTypeId: string): void {
    const selectedTicketTypes = this.passCultureForm.get('selectedTicketTypes') as FormArray;

    if (event.target.checked) {
      selectedTicketTypes.push(new FormControl(ticketTypeId));
      this.selectedTicketTypes.push(ticketTypeId);
    } else {
      const index = this.selectedTicketTypes.indexOf(ticketTypeId);
      if (index >= 0) {
        selectedTicketTypes.removeAt(index);
        this.selectedTicketTypes.splice(index, 1);
      }
    }
  }

  // Category change handler
  onCategoryChange(event: any): void {
    const categoryId = event.target.value;
    this.passCultureForm.get('subcategory').setValue('');
  }

  // Toggle change handler
  onToggleChange(event: any): void {
    const isActive = event.target.checked;
    this.passCultureForm.get('isActive').setValue(isActive);
  }

  // Navigation
  onCancel(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  showSubCategory(subCategory: 'showType' | 'musicType' | 'author' | 'performer' | 'stageDirector' | 'speaker' | 'visa'): boolean {
    const category = this.passCultureForm.get('category').value as EventCategory;
    switch (subCategory) {
        case 'showType':
            const showMatches: EventCategory[] = ['FESTIVAL_SPECTACLE', 'SPECTACLE_REPRESENTATION'];
            return showMatches.includes(category);
        case 'musicType':
            const musicMatches: EventCategory[] = ['CONCERT', 'EVENEMENT_MUSIQUE', 'FESTIVAL_MUSIQUE', 'LIVESTREAM_MUSIQUE', 'LIVESTREAM_EVENEMENT'];
            return musicMatches.includes(category);
        case 'author':
            const authorMatches: EventCategory[] = ['CINE_PLEIN_AIR', 'CONCERT', 'EVENEMENT_CINE', 'EVENEMENT_MUSIQUE', 'FESTIVAL_ART_VISUEL', 'FESTIVAL_CINE', 'FESTIVAL_MUSIQUE', 'FESTIVAL_SPECTACLE', 'LIVESTREAM_EVENEMENT', 'LIVESTREAM_MUSIQUE', 'SEANCE_CINE', 'SPECTACLE_REPRESENTATION'];
            return authorMatches.includes(category);
        case 'performer':
            const performerMatches: EventCategory[] = ['CONCERT', 'EVENEMENT_MUSIQUE', 'FESTIVAL_ART_VISUEL', 'FESTIVAL_MUSIQUE', 'FESTIVAL_SPECTACLE', 'LIVESTREAM_EVENEMENT', 'LIVESTREAM_MUSIQUE', 'SPECTACLE_REPRESENTATION'];
            return performerMatches.includes(category);
        case 'stageDirector':
            const stageDirectorMatches: EventCategory[] = ['CINE_PLEIN_AIR', 'EVENEMENT_CINE', 'FESTIVAL_CINE', 'FESTIVAL_SPECTACLE', 'LIVESTREAM_EVENEMENT', 'SEANCE_CINE'];
            return stageDirectorMatches.includes(category);
        case 'speaker':
            const speakerMatches: EventCategory[] = ['ATELIER_PRATIQUE_ART', 'CONFERENCE', 'RENCONTRE', 'RENCONTRE_EN_LIGNE', 'SALON', 'SEANCE_ESSAI_PRATIQUE_ART'];
            return speakerMatches.includes(category);
        case 'visa':
            const visaMatches: EventCategory[] = ['CINE_PLEIN_AIR', 'EVENEMENT_CINE', 'FESTIVAL_CINE', 'SEANCE_CINE'];
            return visaMatches.includes(category);
        default:
            return false;
    }
  }

  async changeImage(file: File): Promise<void> {
    this.newImage = file;
  }

  updatePassCultureConfig(): void {
    this._subscription.add(
        this.passCultureService.syncEventOffer(this.event._id, this.passCultureForm.value).subscribe(
          async (result) => {
            if (this.newImage) {
              const formData: FormData = new FormData();
              formData.append('image', this.newImage);
              try {
                await this.passCultureService.syncEventOfferImage(this.event._id, formData).toPromise()
                this.notificationService.newNotification({
                  message: 'Votre image a été mise à jour',
                  state: 'success',
                });
              } catch (err) {
                this.notificationService.newNotification({
                  message: this.errorHandler.getError(err),
                  state: 'error',
                });
                this.loadingState$.next();
                return;
              }
            }
            this.loadingState$.next();
            await this.getCurrentEvent(true);
            this.eventService.selected$.next(this.event);
            this.notificationService.newNotification({
              message: 'Votre offre Pass Culture a été mise à jour',
              state: 'success',
            });
          },
          (err: any) => {
            this.notificationService.newNotification({
              message: this.errorHandler.getError(err),
              state: 'error',
            });
            this.loadingState$.next();
          }
        )
      );


  }
}
